import { Directive, ElementRef, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';

/**
 * Convert the input value to integer
 * 
 * if you need just to remove non-numeric digits, without parse the inputed value to Number, use NumberOnlyInputDirective 
 * @see [NumberOnlyInputDirective](number-only.directive.ts)
 * 
 * @ex
 * ```html
 * <!-- value1 is a number -->
 * <input integer [(ngModel)]="value1"/>
 * <!-- value2 is a string of numeric digits -->
 * <input numberOnly [(ngModel)]="value2"/>
 * ```
 */
@Directive({
  standalone: true,
  selector: 'input[integer]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntegerDirective),
      multi: true,
    },
    {
      provide: MAT_INPUT_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntegerDirective),
    },
  ]
})
export class IntegerDirective implements ControlValueAccessor {

  private _value: number | null = null;

  constructor(private hostElement: ElementRef<HTMLInputElement>) { }

  private get viewValue(): string {
    return this.hostElement.nativeElement.value;
  }

  private set viewValue(value: string) {
    this.hostElement.nativeElement.value = value;
  }

  private _formatValue(value: string | number | null) {
    const numeric = typeof value === 'number' ? value : parseInt(value ?? '', 10);
    this.viewValue = Number.isFinite(numeric) ? `${numeric}` : '';
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent) {
    const inputVal = (event.target as HTMLInputElement).value;
    const newValue = parseInt(inputVal, 10);
    this._value = Number.isFinite(newValue) ? newValue : null;
    this._formatValue(newValue);
    this._onChange(this._value);
  }

  @HostListener('blur')
  _onBlur() {
    this._onTouched();
  }

  writeValue(value: any): void {
    this._value = isFinite(value) ? value : null;
    this._formatValue(this._value);
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.hostElement.nativeElement.disabled = isDisabled;
  }

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};
}

